import styled from '@emotion/styled';
import { Paper, Typography } from '@material-ui/core';
import { ChangeEvent, ClassAttributes, createElement, FC, useContext, useEffect } from 'react';
import { FormattedHTMLMessage, FormattedMessage } from 'react-intl';

import Language, { AVAILABLE_LANGUAGES } from '../../entity/Language';
import { BusinessRegionAdRegionKey, OrganizationDetailFragment } from '../../generated/graphql';
import theme from '../../theme';
import getPointConfirmationLinkUrl from '../../utility/getPointConfirmationLinkUrl';
import ExternalAccountsTabs from '../component/compound/ExternalAccountsTabs';
import PurchasePointButton from '../component/compound/PurchasePointButton';
import Select, { SelectItem } from '../component/compound/Select';
import Spinner from '../component/compound/Spinner';
import LanguageComponent from '../component/primitive/Language';
import { SPACING_HUGE, SPACING_REGULAR, SPACING_UNIT } from '../constant/spacing';
import { context as ConfigContext } from '../context/config';
import { gridLayoutContainer } from '../styleUtility/gridLayout';
import mq from '../styleUtility/mediaquery';

type Props = ClassAttributes<HTMLElement> & {
  loading: boolean;
  language: Language;
  region: BusinessRegionAdRegionKey;
  point: number | null;
  organizationId: string;
  availableOrganizations: Array<OrganizationDetailFragment>;
  selectedOrganization: OrganizationDetailFragment | null;
  isOpendByIframe: boolean;
  onChangeSelectedOrganization: (e: ChangeEvent<HTMLElement>, organization: OrganizationDetailFragment) => void;
  onChangeLanguage: (e: ChangeEvent<HTMLElement>, language: Language) => void;
  onMount: () => void;
  onUnmount: () => void;
  className?: string;
};

const ConfigurationPage: FC<Props> = ({
  loading,
  language,
  region,
  point,
  organizationId,
  onChangeLanguage,
  onMount,
  onUnmount,
  className,
}) => {
  const config = useContext(ConfigContext);

  useEffect(() => {
    onMount();
    return onUnmount();
    // TODO: Implementation before introducing rules
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return loading ? (
    <Loading>
      <Spinner />
    </Loading>
  ) : (
    <Root className={className}>
      <PageHeading variant="h4" color="primary" noWrap>
        <FormattedMessage id="configuration_page.page_heading" />
      </PageHeading>

      <Section>
        <SectionHeading variant="h5" color="textSecondary">
          <FormattedMessage id="configuration_page.account_settings" />
        </SectionHeading>

        <Language>
          <LanguageSelect
            label={<FormattedMessage id="configuration_page.language_heading" />}
            defaultValue={language}
            onChange={onChangeLanguage as any}
          >
            {AVAILABLE_LANGUAGES.map((language) => (
              <SelectItem value={language} key={language}>
                <LanguageComponent language={language} />
              </SelectItem>
            ))}
          </LanguageSelect>

          {point !== null && organizationId ? (
            <PointWrapper>
              <SubHeading variant="subtitle1">
                <FormattedMessage id="configuration_page.point_subheading" />
              </SubHeading>
              <Point>
                <PointLabel>
                  <FormattedMessage id="configuration_page.point_label" />
                </PointLabel>
                <PointBalance>
                  {point.toLocaleString()}
                  <PointUnit>P</PointUnit>
                </PointBalance>
                <div>
                  <PurchasePointButton organizationId={organizationId} />
                </div>
              </Point>
              <Typography>
                <FormattedHTMLMessage
                  id="configuration_page.point_note"
                  values={{
                    url: getPointConfirmationLinkUrl(config.ID_WEB_URL, organizationId),
                  }}
                />
              </Typography>
            </PointWrapper>
          ) : null}
        </Language>
      </Section>

      <Section>
        <SectionHeading variant="h5" color="textSecondary">
          <FormattedMessage id="configuration_page.external_account_connect_settings" />
        </SectionHeading>
        <ExternalAccountsTabs region={region} />
      </Section>
    </Root>
  );
};

const Root = styled('div')({
  padding: `${SPACING_UNIT * 10}px calc((100% - 960px) / 2)`,
  [mq('sp')]: {
    padding: 16,
  },
});

const Loading = styled('div')({
  padding: SPACING_HUGE,
});

const Section = styled('section')({
  marginBottom: SPACING_HUGE * 2,
});

const PageHeading = styled(Typography)({ marginBottom: SPACING_UNIT * 5 });

const SectionHeading = styled(Typography)({ marginBottom: SPACING_UNIT * 3 });

const SubHeading = styled(Typography)({ marginBottom: SPACING_REGULAR });

const Language = styled(Paper)({
  padding: SPACING_REGULAR,
});

const LanguageSelect = styled(Select)({ width: 192 });

const PointWrapper = styled('div')({
  marginTop: SPACING_UNIT * 3,
  paddingTop: SPACING_UNIT * 2,
  borderTop: `1px solid ${theme.palette.grey['200']}`,
});

const Point = styled('div')(
  gridLayoutContainer({
    columns: ['100px', '100px', 'auto'],
    rows: ['auto'],
    areas: [['.', '.', '.']],
    alignItems: 'center',
  }),
  {
    marginBottom: SPACING_UNIT * 2,
    [mq('sp')]: {
      display: 'flex',
      flexDirection: 'column',
    },
  },
);

const PointLabel = styled(Typography)({
  fontSize: 14,
});

const PointBalance = styled(Typography)({
  fontSize: 18,
  fontWeight: 700,
});
PointBalance.defaultProps = { color: 'primary' };

const PointUnit = styled('span')({
  fontSize: 14,
  display: 'inline-block',
  fontWeight: 700,
  marginLeft: 2,
  color: 'rgba(0, 0, 0, 0.54)',
});

export default ConfigurationPage;
